import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import {
  BlueBirdTheme,
  Button,
  Filter,
  Loading,
  Tab,
  Typography,
} from "@kitbird/core";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { addDays, differenceInDays } from "date-fns";
import { CardContent } from "../../../components/Content/Content.style";
import logo from "../../../assets/icons/logogram_bb.png";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download_icon.svg";
import { ReactComponent as PrintIcon } from "../../../assets/icons/printer.svg";
import { ReactComponent as FilterIcon } from "../../../assets/icons/filter.svg";
import { StickyTable } from "../../../components/Table/Table.style";
import { IOptionItem } from "../../../typings/common";
import Table from "../../../components/Table";
import useCreateReceiptSubmission from "./hooks/useCreateReceiptSubmission";
import { CustomDatePicker, DatePickerWrapper } from "../../../components/CustomDatePicker.style";
import { statusBonOptions } from "./constants";
import FilterDropdown from "../../../components/FilterDropdown";
import { DepartmentItemI, IGetTrxData, PoolItemI } from "../../../services/trxService";
import columns from "./columns";
import useAuth from "../../../components/Authentication/useAuth";
import {
  PageType,
  TabTypes,
  ApprovalModalType,
} from "./enums";
import SearchInput from "../../../components/SearchInput";
import { ActionSection } from "../../../components/Filter/Filter.style";
import CodeTypeOpts from "../../../enums/common";
import getHasAccessAction from "../../../components/Authentication/utils";

const { gray } = BlueBirdTheme.colors;

interface SubmissionTableI {
  pageType: PageType,
  setPageType: React.Dispatch<React.SetStateAction<PageType>>,
  setOpen: React.Dispatch<React.SetStateAction<ApprovalModalType | false>>,
  setDetailRow: React.Dispatch<React.SetStateAction<IGetTrxData | undefined>>,
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>,
}

const DownloadAction = styled.div`
  display: flex;
  gap: 16px;
`;

const tabOpts = [
  {
    label: "Menunggu persetujuan",
    name: TabTypes.OPEN,
  },
  {
    label: "Disetujui",
    name: TabTypes.CLOSE,
  },
  {
    label: "Ditolak",
    name: TabTypes.CANCEL,
  },
];

const SubmissionTable = ({
  pageType, setPageType, setOpen, setDetailRow, useCustomHook,
}: SubmissionTableI) => {
  const {
    meta,
    trxParams,
    setTrxParams,
    searchText,
    setSearchText,
    tab,
    setTab,
    isCreator,
    isApprover,
    isPoolsDepartmentsLoading,
    columnOpts,
    setColumnOpts,
    hiddenColumn,
    dataAllDepartment,
    seeAllDepartmentOpt,
    currPoolOpts,
    currDepartmentOpts,
    setCurrDepartmentOpts,
    dataCompanies,
    isCompaniesLoading,
    transactionPages,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    handleDownloadCsv,
    handleDownloadPdf,
    setPrintModal,
    seeAllCompanyOpt,
  } = useCustomHook;
  const location = useLocation();
  const { rbacInfo, userAccount } = useAuth();
  const [temporaryMaxDate, setTemporaryMaxDate] = useState<null | Date>();

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));
  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  const handleChangeJobFlow = () => {
    // change jobflow type manually
    if (isCreator && isApprover) {
      setTab((curr) => (curr === TabTypes.CREATED ? TabTypes.OPEN : TabTypes.CREATED));
    }
  };

  const handlePoolChange = (val: IOptionItem) => {
    let departmentOpts: { label: string, value: string }[] = [];
    if (dataAllDepartment && dataAllDepartment.length) {
      departmentOpts = dataAllDepartment.map(
        (item) => ({ label: item.department, value: item.department }),
      );
      setCurrDepartmentOpts(departmentOpts);
    } else {
      departmentOpts = userAccount?.userPools.filter(
        (pool) => pool.poolId === val.value,
      )[0].departments.map((department) => ({
        label: department,
        value: department,
      })) || [];
      setCurrDepartmentOpts(departmentOpts);
    }

    const newParams = {
      pool: val as { label: string, value: string },
      company: seeAllCompanyOpt,
    };
    setTrxParams(
      (curr) => ({ ...curr, ...newParams }),
    );
  };

  const handleChangeDate = (dates: (Date | null)[] | Date | null) => {
    if (Array.isArray(dates) && dates[0] !== null && dates[1] === null) {
      setTemporaryMaxDate(
        addDays(
          dates[0],
          Math.min(differenceInDays(new Date(), dates[0]), 30),
        ),
      );
    } else if (Array.isArray(dates) && dates[1] !== null) {
      setTemporaryMaxDate(new Date());
    }
  };

  const handleSubmitDate = (dates?: Date[] | null) => {
    setTemporaryMaxDate(new Date());
    setTrxParams((params) => ({
      ...params,
      dates: dates || (
        pageType === PageType.HISTORY_LIST
          ? [new Date(), new Date()]
          : undefined
      ),
    }));
  };

  const handleResetDate = () => setTrxParams((curr) => ({ ...curr, dates: undefined }));

  const hasAccessCodeTypeFilter = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow enable filter code type",
  });

  const tabComponent = isApprover && pageType !== PageType.HISTORY_LIST && <Tab
    onClickTab={(tabName: string) => { setTab(tabName as TabTypes); }}
    items={tabOpts}
    variant="fullWidth"
    active={tabOpts.findIndex((opt) => opt.name === tab)}
  />;

  const columnFilter = <Filter
    label="&nbsp;&nbsp;&nbsp;Kolom"
    icon={<FilterIcon />}
    options={columnOpts}
    onSelected={setColumnOpts}
  />;

  const optionsCompanies = useMemo(() => dataCompanies?.lstmstbusiness.map((company) => (
    { label: company.company_code, value: company.company_code }
  )) || [], [dataCompanies]);

  const filtersComponent = <ActionSection>
    <ActionSection>
      <SearchInput
        placeholder="Cari data"
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
      />
      <DatePickerWrapper>
        <CustomDatePicker
          variant="withRange"
          label="Tanggal"
          startDate={trxParams.dates ? trxParams.dates[0] : undefined}
          endDate={trxParams.dates ? trxParams.dates[1] : undefined}
          onChange={handleChangeDate}
          onReset={handleResetDate}
          onSubmit={handleSubmitDate}
          maxDate={pageType === PageType.HISTORY_LIST
            ? temporaryMaxDate || new Date()
            : new Date()
          }
        />
      </DatePickerWrapper>
      {pageType === PageType.HISTORY_LIST
        && (
          <>
            <FilterDropdown
              id="company"
              label="Perusahaan"
              value={trxParams.company?.label || ""}
              onSelected={(val: IOptionItem) => setTrxParams(
                (curr) => ({
                  ...curr,
                  company: val as { label: string, value: string },
                }),
              )}
              options={[seeAllCompanyOpt, ...optionsCompanies]}
              disable={isCompaniesLoading || undefined}
            />
            <FilterDropdown
              id="code_type"
              label="Code type"
              value={trxParams.codeType?.label || ""}
              onSelected={(val: IOptionItem) => setTrxParams(
                (curr) => ({
                  ...curr,
                  codeType: val as { label: string, value: string },
                  company: undefined,
                }),
              )}
              options={hasAccessCodeTypeFilter ? [CodeTypeOpts[1]] : []}
              disable={!hasAccessCodeTypeFilter || undefined}
            />
            <FilterDropdown
              id="pool"
              label="Pool"
              value={trxParams.pool?.label || ""}
              onSelected={handlePoolChange}
              options={currPoolOpts}
              disable={isPoolsDepartmentsLoading || undefined}
            />
            <FilterDropdown
              id="department"
              label="Departemen"
              value={trxParams.department?.label || ""}
              onSelected={(val: IOptionItem) => setTrxParams(
                (curr) => ({
                  ...curr,
                  department: val as { label: string, value: string },
                }),
              )}
              options={
                seeAllDepartmentOpt
                  ? [seeAllDepartmentOpt, ...currDepartmentOpts]
                  : currDepartmentOpts
              }
              disable={isPoolsDepartmentsLoading || undefined}
            />
            <FilterDropdown
              id="status-bon"
              label="Status bon"
              value={trxParams.status_bon?.label}
              onSelected={(val: IOptionItem) => setTrxParams(
                (curr) => ({ ...curr, status_bon: val as { label: string, value: "CLOSE" | "CANCEL" | "OPEN" } }),
              )}
              options={statusBonOptions}
              width="125px"
            />
          </>
        )
      }
      {pageType === PageType.LIST && columnFilter}
    </ActionSection>
    {(isCreator && pageType !== PageType.HISTORY_LIST) && <Button
      leftIcon={<PlusIcon />}
      onClick={() => setPageType(PageType.CREATE)}
    >
      Tambah data
    </Button>}
    {(pageType === PageType.HISTORY_LIST)
      && <DownloadAction>
        <Button
          leftIcon={<DownloadIcon />}
          variant="secondary"
          onClick={handleDownloadPdf}
        >
          .PDF
        </Button>
        <Button
          leftIcon={<DownloadIcon />}
          variant="secondary"
          onClick={handleDownloadCsv}
        >
          .CSV
        </Button>
        <Button
          leftIcon={<PrintIcon />}
          variant="primary"
          onClick={() => setPrintModal(true)}
        >
          Print
        </Button>
      </DownloadAction>}
  </ActionSection>;

  // TODO
  return (
    <CardContent>
      <div style={{ display: "flex", gap: 9, justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 9, fontWeight: 500 }}
          onClick={handleChangeJobFlow}
        >
          <img src={logo} width={32} height={26} />
          <Typography level={4} fontWeight="medium" color={gray.subtitle}>Daftar {location.pathname.includes("riwayat-pengajuan-bon") && "riwayat"} pengajuan bon</Typography>
        </div>
        {location.pathname.includes("riwayat-pengajuan-bon") && columnFilter}
      </div>
      <div>
        {tabComponent}
        {filtersComponent}
      </div>
      <StickyTable ref={tableRef}>
        {isFetching && !isFetchingNextPage
          ? <Loading variant="line" />
          : <Table
            dataSource={transactionPages && transactionPages.pages.flatMap((page: any) => page)}
            columns={columns(
              setPageType,
              setOpen,
              setDetailRow,
              pageType,
              tab,
              isCreator,
              isApprover,
              userAccount.userDepartments.includes("SKA"),
            )}
            hiddenColumnKeys={hiddenColumn}
            footer={
              <>
                <tfoot ref={obsRef} style={{ height: 5, width: "100%" }} />
                {isFetchingNextPage && <Loading variant="circular" />}
              </>
            }
            sortConfig={{
              sortBy: trxParams.sortBy,
              sortType: trxParams.sortType,
              onSort: (sortBy, sortType) => setTrxParams((curr) => ({ ...curr, sortBy, sortType })),
            }}
            withNumber
            onClickRow={(row) => {
              setDetailRow(row as IGetTrxData);
              setPageType((curr) => (curr === PageType.HISTORY_LIST
                ? PageType.HISTORY_DETAIL
                : PageType.DETAIL));
            }}
          />
        }
      </StickyTable>
    </CardContent>
  );
};

export default SubmissionTable;
